import { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import dxLogo from '../assets/image/dxinterior-logo.svg';
import loginBackground from '../assets/image/login-bg.png';

/**
 * Login page - minimal, two-column layout
 * Left: form (DX LIVING, WELCOME BACK, email, password, remember me, forgot link, LOGIN)
 * Right: photorealistic interior background
 * Integrates with DXLiving auth API (JWT).
 */
const Login = () => {
  const { login } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      await login({ email, password }, rememberMe);
    } catch (err) {
      setError(err.message || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left: Form panel */}
      <div className="w-full md:w-[42%] lg:w-[50%] bg-white flex flex-col p-8 md:p-12 lg:p-8">
        {/* Logo - top left */}
        <a href="/" className="shrink-0 self-start" aria-label="Go to landing page">
          <img src={dxLogo} alt="DX Interiors" className="h-12 w-auto" />
        </a>

        <div className="flex flex-col flex-1 justify-center max-w-sm mx-auto w-full">
          <h2 className="text-2xl md:text-[42px] title-heading text-center text-gray-900 mb-2">
            WELCOME BACK
          </h2>
          <p className="text-gray-500 text-md mb-8 text-center">
            Please enter your details below.
          </p>

          <form onSubmit={handleSubmit} className="space-y-6 mt-8">
            {error && (
              <div className="text-sm text-red-600 bg-red-50 px-4 py-3 rounded border border-red-200">
                {error}
              </div>
            )}
            <div>
              <label htmlFor="email" className="sr-only">
                Email address
              </label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Email address"
                className="w-full py-3 text-gray-900 placeholder-gray-400 bg-transparent border-b border-gray-300 focus:outline-none focus:border-gray-900 transition-colors"
                autoComplete="email"
                required
              />
            </div>

            <div className="relative">
              <label htmlFor="password" className="sr-only">
                Password
              </label>
              <input
                id="password"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Password"
                className="w-full py-3 pr-10 text-gray-900 placeholder-gray-400 bg-transparent border-b border-gray-300 focus:outline-none focus:border-gray-900 transition-colors"
                autoComplete="current-password"
                required
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-0 top-1/2 -translate-y-1/2 p-2 text-gray-400 hover:text-gray-600 focus:outline-none"
                aria-label={showPassword ? 'Hide password' : 'Show password'}
              >
                {showPassword ? (
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
                    <line x1="1" y1="1" x2="23" y2="23" />
                  </svg>
                ) : (
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                    <circle cx="12" cy="12" r="3" />
                  </svg>
                )}
              </button>
            </div>

            <div className="flex items-center justify-between text-sm">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                  className="w-4 h-4 rounded border-gray-300 text-gray-900 focus:ring-gray-500"
                />
                <span className="text-gray-600">Remember me</span>
              </label>
              <a
                href="#"
                className="text-gray-500 hover:text-gray-700 underline underline-offset-2"
              >
                Forgot password
              </a>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full py-3 px-4 mt-4 border border-gray-300 text-gray-900 font-medium tracking-wide hover:bg-gray-50 disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
            >
              {loading ? 'Logging in…' : 'LOGIN'}
            </button>
          </form>
        </div>
      </div>

      {/* Right: Interior image panel */}
      <div className="hidden md:block flex-1 relative overflow-hidden">
        <img
          src={loginBackground}
          alt="Modern interior design"
          className="absolute inset-0 w-full h-full object-cover animate-slide-fade-in-right"
        />
      </div>
    </div>
  );
};

export default Login;
